import { type FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronDownIcon } from '@heroicons/react/24/outline';
import cx from 'classnames';

import { FEED_PANEL_ID, type FeedView, SORT_ENABLED } from '~/constants/community';

import { MobileSortSheet } from './MobileSortSheet';
import { SortOrderDropdown } from './SortOrderDropdown';

type ActiveTabProps = {
  type: 'button';
  role: 'tab';
  'aria-selected': true;
  'aria-controls': string;
  className: string;
  onClick: () => void;
};

interface FeedTabBarProps {
  activeTab: FeedView;
  onTabChange: (tab: FeedView) => void;
  variant?: 'default' | 'sort-trigger';
}

const TABS: FeedView[] = ['for_you', 'following'];

const tabClassName = (isActive: boolean) =>
  cx(
    'flex items-center h-10 rounded-full text-sm font-medium text-black tracking-[-0.14px] whitespace-nowrap transition-colors',
    {
      'bg-white border border-black/5 px-4 shadow-[0px_4px_12px_0px_rgba(0,0,0,0.08)] gap-1.5': isActive,
      'opacity-50 px-5': !isActive,
    },
  );

const renderActiveTabAsSortTrigger = (label: string, tabProps: ActiveTabProps) => {
  const buttonContent = (
    <>
      {label}
      <ChevronDownIcon className="size-3.5 text-black/40" strokeWidth={2} aria-hidden="true" />
    </>
  );

  return (
    <>
      <SortOrderDropdown
        label={label}
        className="hidden md:contents"
        buttonProps={tabProps}
        buttonContent={buttonContent}
      />
      <MobileSortSheet
        label={label}
        className="contents md:hidden"
        buttonProps={tabProps}
        buttonContent={buttonContent}
      />
    </>
  );
};

export const FeedTabBar: FC<FeedTabBarProps> = ({ activeTab, onTabChange, variant = 'default' }) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between px-6 py-4 w-full">
      <div className="w-[292px] shrink-0 hidden md:block" />
      <div role="tablist" className="flex items-center gap-2 mx-auto md:mx-0">
        {TABS.map((tab) => {
          const isActive = activeTab === tab;
          const label = t(`community.feed.tab_${tab}`);

          if (isActive && variant === 'sort-trigger' && SORT_ENABLED) {
            const tabProps: ActiveTabProps = {
              type: 'button',
              role: 'tab',
              'aria-selected': true,
              'aria-controls': FEED_PANEL_ID,
              className: tabClassName(true),
              onClick: () => onTabChange(tab),
            };

            return <Fragment key={tab}>{renderActiveTabAsSortTrigger(label, tabProps)}</Fragment>;
          }

          return (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={FEED_PANEL_ID}
              className={tabClassName(isActive)}
              onClick={() => onTabChange(tab)}
            >
              {label}
            </button>
          );
        })}
      </div>
      <div className="w-[292px] shrink-0 hidden md:block" />
    </div>
  );
};
